import type { Block } from '@/types/blocks';
import type { AppearanceFields } from '@/features/themes/lib/appearance';
import { appearanceCssVars } from '../lib/brandingVars';
import { PublicBlockRenderer } from './PublicBlockRenderer';

interface PublicFooterProps {
  siteName: string;
  siteId?: string;
  siteSlug?: string;
  /** Quando definido (modelo de footer ativado em Aparência), substitui totalmente o footer padrão abaixo. */
  customBlocks?: Block[] | null;
  customAppearance?: AppearanceFields | null;
}

export function PublicFooter({ siteName, siteId, siteSlug, customBlocks, customAppearance }: PublicFooterProps) {
  if (customBlocks && customBlocks.length > 0) {
    const customStyle = customAppearance
      ? {
          ...appearanceCssVars(customAppearance),
          backgroundColor: 'var(--color-page-bg)',
          backgroundImage: 'var(--bg-image-page)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : undefined;

    return (
      <footer className="w-full" style={customStyle}>
        <PublicBlockRenderer blocks={customBlocks} siteId={siteId} siteSlug={siteSlug} />
      </footer>
    );
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const platformHostname = (import.meta.env.VITE_PLATFORM_HOSTNAME as string | undefined) ?? 'lumenise.app';
  const isPlatformHost = hostname === platformHostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.pages.dev');
  const legalBase = siteSlug && isPlatformHost ? `/s/${siteSlug}/legal` : '/legal';

  return (
    <footer
      className="w-full"
      style={{
        background: 'linear-gradient(180deg, var(--color-primary), color-mix(in oklab, var(--color-primary), #000 30%))',
      }}
    >
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'var(--color-accent)' }} />
              <span className="font-display text-xl font-bold" style={{ color: '#fff' }}>{siteName}</span>
            </div>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">
              Tecnologia, inovação e inteligência artificial a serviço de pessoas.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <a href={`${legalBase}/privacidade`} className="text-white/70 transition-colors hover:text-white/100">Privacidade</a>
            <a href={`${legalBase}/termos`} className="text-white/70 transition-colors hover:text-white/100">Termos</a>
            <a href="/app" className="text-white/70 transition-colors hover:text-white/100">Admin</a>
          </div>
        </div>
        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-white/50">
          © {new Date().getFullYear()} {siteName}. Feito com Lumenise CMS.
        </div>
      </div>
    </footer>
  );
}
